<template>
  <div
    class="channel-header flex flex-align-center"
    :class="{ 'SB-border bottom': !noBorder }"
    :style="cssVars" >
    <div
      class="logo-wrapper"
      :class="{ basic: backgroundColor === 'transparent' }" >

      <SBIcon
        v-if="icon"
        :icon="icon"
        :color="iconColor"
        :size="28"/>
      <div
        v-else
        class="image-wrapper" />
    </div>
    <div class="title-wrapper greedy flex column">
      <h3 class="ellipsis">{{ title }}</h3>
      <a
        v-if="link"
        :href="link"
        target="_blank"
        class="link flex flex-align-center">
        <p class="">Learn more</p>
        <SBIcon
          class="ml-1"
          :icon="SBIconList.ARROW"
          :rotate="225"
          :color="SBColors.blackUltralight"
          :hover-icon-color="SBColors.blackPrimary"
          :size="10"
          :pointer="true" />
      </a>
    </div>
    <button
      v-if="active !== null"
      :class="{ active }"
      class="channel-action btn flex center-all btn-secondary"
      @click="activation()" >
      <SBIcon
        class="mr-2"
        :icon="active ? SBIconList.TRASH : SBIconList.PLUS"
        :color="SBColors.blackPrimary"
        :size="active ? 12 : 8"
        :pointer="true" />
      <p>{{ active ? 'Disconnect' : 'Connect' }}</p>
    </button>
    <button
      v-if="button"
      :class="{ colored: button.colored }"
      class="channel-button btn flex center-all btn-secondary"
      @click="button.action" >
      {{ button.label }}
      <SBIcon
        v-if="button.icon"
        class="ml-1"
        :icon="button.icon"
        :color="button.iconColor ? button.iconColor : SBColors.blackPrimary"
        :rotate="225"
        :size="10" />
    </button>
  </div>
</template>

<script>
import SBColors from '@/helpers/lists/colors';
import SBIcon from '@/components/common/Icon';
import SBIconList from '@/helpers/lists/sb-icon-list';

export default {
  name: 'ChannelHeader',
  components: {
    SBIcon,
  },
  props: {
    /**
     * Use this prop if you need multicolour image or if jou want to use the prepared icon exactly as it is saved.
     * Set full path without the leading '@/`.
     */
    image: {
      type: String,
      reqired: false,
      default: null,
    },
    /** Has priority over image prop. If both set then icon will be shown. */
    icon: {
      type: Object,
      required: false,
      default: null,
    },
    iconColor: {
      type: String,
      required: false,
      default: SBColors.blackPrimary,
    },
    backgroundColor: {
      type: String,
      reqiored: false,
      default: SBColors.greyChatBubble,
    },
    title: {
      type: String,
      required: true,
      default: '',
    },
    link: {
      type: String,
      required: false,
      default: null,
    },
    active: {
      type: Boolean,
      required: false,
      default: null,
    },
    button: {
      type: Object,
      required: false,
      default: () => {},
    },
    noBorder: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      SBColors,
      SBIconList,
    };
  },
  computed: {
    /**
     * Css variables to be included to css.
     */
    cssVars() {
      return {
        '--background-color': this.backgroundColor,
        '--background-image': `url(${this.iconData})`,
        '--button-hover-color': this.button && this.button.hoverIconColor ? this.button.hoverIconColor : SBColors.blackPrimary,
      };
    },
    iconData() {
      // NOTE: For some reason it has to be done like this from the computed. if we fapp "@/" directly on image prop then the require fails.
      // eslint-disable-next-line
      return this.image ? (this.image.startsWith('http') ? this.image : require(`@/${this.image}`)) : null;
    },
  },
  methods: {
    activation() {
      if (this.active) {
        this.$emit('deactivate');
      } else {
        this.$emit('activate');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
a {
  text-decoration: none;
}

.channel-header {
  padding-bottom: 35px;
}
.logo-wrapper {
  width: 58px;
  height: 58px;
  padding: 14px;
  margin-right: 10px;
  border-radius: 12px;
  box-shadow: 0 0 3px 0 rgba(48, 54, 70, 0.16);
  border: solid 1px $greyBorder;
  background-color: var(--background-color);

  &.basic {
    padding: 0;
    border: none;
    box-shadow: none;
    width: 22px;
    height: 22px;
  }

  .image-wrapper {
    width: 100%;
    height: 100%;
    background-image: var(--background-image);
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
  }
}

.title-wrapper {
  min-width: 0;
}

.channel-action, .channel-button {
  max-height: 30px;
  font-size: 14px;
  font-weight: 500;
  margin-left: 10px;
  padding: 7px 16px 7px 16px;
  border-radius: 6px;
  box-shadow: 0 1px 3px 0 rgba(48, 54, 70, 0.15);
  border: solid 1px $greyBorder;
  border-color: $greyBorder !important;
  cursor: pointer;
}

.channel-action {
  background-image: linear-gradient(108deg, #fffff4, #fff7fd 31%, #f7ffff 67%, #fafffa);
  &.active {
    background-image: none !important;
    background-color: #fff8fa !important;
    border-radius: 6px;
    border: solid 1px $greyBorder;

    p {
      color: $blackPrimary;
    }
  }

  &:hover {
    box-shadow: 0 1px 3px 0 rgba(48, 54, 70, 0.3);
  }
}

.colored {
  background-image: linear-gradient(108deg, #fffff4, #fff7fd 31%, #f7ffff 67%, #fafffa);
}


.channel-button {
  display: flex;
  align-items: center;

  &:hover {
    box-shadow: 0 0 3px 0 rgba(48, 54, 70, 0.3);
    ::v-deep .sb-icon span.icon.mask {
      background-color: var(--button-hover-color) !important;
    }
  }
}

h3 {
  font-size: 18px;
  font-weight: 500;
  color: $blackPrimary;
  margin-bottom: 5px;
}

.link {
  cursor: pointer;
  width: fit-content;

  * {
    font-size: 14px;
    font-weight: 400;
    color: $blackUltralight;
  }

  &:hover * {
    color: $blackPrimary;
  }
}

::v-deep .link:hover .icon.mask {
  background-color: $blackPrimary !important;
}
</style>
